import {
    addLogicNode,
    getLogicNodeLocation,
    isLogicContainerNode,
    type AddLogicNodeMeta,
    type LogicNode,
    type LogicTreeChange,
    type TradeLogicNodeId
} from '../model';

export type DraftInsertionTargetMode = 'root' | 'inside-selected-container' | 'after-selected-node';

export interface DraftInsertionTarget {
    parentId: TradeLogicNodeId | null;
    index?: number;
    mode: DraftInsertionTargetMode;
    selectedNodeId: TradeLogicNodeId | null;
    selectedNodeLabel: string | null;
    selectedNodeKind: LogicNode['kind'] | null;
    parentLabel: string | null;
    parentKind: LogicNode['kind'] | null;
    label: string;
    detail: string;
}

export const resolveDraftInsertionTarget = (rootNode: LogicNode | null, selectedNodeId: TradeLogicNodeId | null): DraftInsertionTarget => {
    const fallbackParent = rootNode && isLogicContainerNode(rootNode) ? rootNode : null;
    const fallbackTarget = createRootInsertionTarget(fallbackParent);

    if (!rootNode || !selectedNodeId) {
        return fallbackTarget;
    }

    const selectedLocation = getLogicNodeLocation(rootNode, selectedNodeId);

    if (!selectedLocation) {
        return fallbackTarget;
    }

    const selectedNode = selectedLocation.node;

    if (isLogicContainerNode(selectedNode)) {
        const selectedLabel = formatNodeLabel(selectedNode);

        return {
            parentId: selectedNode.id,
            mode: 'inside-selected-container',
            selectedNodeId: selectedNode.id,
            selectedNodeLabel: selectedLabel,
            selectedNodeKind: selectedNode.kind,
            parentLabel: selectedLabel,
            parentKind: selectedNode.kind,
            label: `Inside ${selectedLabel}`,
            detail: `End of selected ${formatNodeKind(selectedNode.kind)}`
        };
    }

    if (!selectedLocation.parent) {
        return fallbackTarget;
    }

    const parentLabel = formatNodeLabel(selectedLocation.parent);
    const selectedLabel = formatNodeLabel(selectedNode);

    return {
        parentId: selectedLocation.parent.id,
        index: selectedLocation.path[selectedLocation.path.length - 1] + 1,
        mode: 'after-selected-node',
        selectedNodeId: selectedNode.id,
        selectedNodeLabel: selectedLabel,
        selectedNodeKind: selectedNode.kind,
        parentLabel,
        parentKind: selectedLocation.parent.kind,
        label: `After ${selectedLabel}`,
        detail: `Within ${parentLabel}`
    };
};

export const appendNodeAtResolvedInsertionTarget = (
    rootNode: LogicNode | null,
    node: LogicNode,
    selectedNodeId: TradeLogicNodeId | null
): LogicTreeChange<AddLogicNodeMeta | null> => {
    if (!rootNode) {
        return addLogicNode(rootNode, node, {});
    }

    const insertionTarget = resolveDraftInsertionTarget(rootNode, selectedNodeId);
    const parentId = insertionTarget.parentId ?? (isLogicContainerNode(rootNode) ? rootNode.id : null);

    return addLogicNode(rootNode, node, {
        parentId,
        index: insertionTarget.index
    });
};

const createRootInsertionTarget = (rootNode: LogicNode | null): DraftInsertionTarget => {
    if (!rootNode) {
        return {
            parentId: null,
            mode: 'root',
            selectedNodeId: null,
            selectedNodeLabel: null,
            selectedNodeKind: null,
            parentLabel: null,
            parentKind: null,
            label: 'New root',
            detail: 'First node in the draft'
        };
    }

    const rootLabel = formatNodeLabel(rootNode);

    return {
        parentId: rootNode.id,
        mode: 'root',
        selectedNodeId: null,
        selectedNodeLabel: null,
        selectedNodeKind: null,
        parentLabel: rootLabel,
        parentKind: rootNode.kind,
        label: rootLabel,
        detail: `End of ${formatNodeKind(rootNode.kind)}`
    };
};

const formatNodeLabel = (node: LogicNode) => node.label?.trim() || node.id;

const formatNodeKind = (kind: LogicNode['kind']) => `${kind} node`;
